<div class="compare-modal position-relative">
  <button
    type="button"
    class="close position-absolute"
    style="top: 12px; right: 16px; z-index: 10;"
    aria-label="Close"
    (click)="activeModal.dismiss('Cross click')"
  >
    <span aria-hidden="true">&times;</span>
  </button>
  
  <h3>Amazon repricing for {{ item.sku }}</h3>
  <p class="subtitle">
    See how your calculated prices differ from Amazon ones.
  </p>

  <ngb-accordion>
    <ngb-panel id="panel-3">
      <ng-template ngbPanelHeader let-opened="opened">
        <div class="container-fluid d-flex justify-content-left">
          <button
            ngbPanelToggle
            class="btn text-left pl-0 pr-0 mr-1 w-100 d-flex justify-content-between align-items-center outline-0 border-0"
          >
            <strong>Recommendation Analysis</strong>
            <i *ngIf="!opened" class="fas fa-angle-down ml-2 mr-1"></i>
            <i *ngIf="opened" class="fas fa-angle-up ml-2 mr-1"></i>
          </button>
        </div>
      </ng-template>
      <ng-template ngbPanelContent>
        <app-repricing-recommend-analysis-content
          [repricingItem]="repricingItem"
          [isFitGrid]="true"
        >
        </app-repricing-recommend-analysis-content>
      </ng-template>
    </ngb-panel>
  </ngb-accordion>

  <div class="comparison-container">
    <div class="price-card amazon">
      <div class="price-loading__overlay" [ngClass]="!isLoading && 'hidden'">
        <span class="spinner-border spinner-border-sm"></span>
      </div>
      <h4>Amazon Seller Central</h4>

      <!-- Main Price Block -->
      <div style="margin-bottom: 1rem" *ngIf="repricingItem?.isSaleActive">
        <span class="label-xs gray">Sale Price</span>
        <div class="price-row">
          <!-- Amazon Price Display -->
          <span class="price-large text-slate-900">{{
            getDisplayCurrency(repricingItem?.currentSalePrice)
          }}</span>

          <app-discount-tag
            [originalText]="'Your Price'"
            [originalPrice]="repricingItem?.currentInboundPrice"
            [salePrice]="repricingItem?.currentSalePrice"
            [hardSavePercent]="repricingItem?.saleSavePercent"
            [displayCurrency]="displayCurrency"
          ></app-discount-tag>
        </div>
      </div>

      <div style="margin-bottom: 1rem">
        <span class="label-xs gray">Your Price</span>
        <div class="price-row">
          <!-- Amazon Price Display -->
          <span class="price-large text-slate-900">{{
            getDisplayCurrency(repricingItem?.currentInboundPrice)
          }}</span>
        </div>
      </div>

      <!-- Details Grid -->
      <div class="details-grid">
        <div class="detail-card">
          <div class="detail-label">Min Price - Max Price</div>
          <div class="detail-value">
            {{ getDisplayCurrency(repricingItem?.currentMin) }} -
            {{ getDisplayCurrency(repricingItem?.currentMax) }}
          </div>
        </div>
      </div>
    </div>

    <div class="arrow">
      <span>→</span>
    </div>

    <div class="price-card incoming" [ngClass]="isWarning && 'warning'">
      <div class="price-loading__overlay" [ngClass]="!isLoading && 'hidden'">
        <span class="spinner-border spinner-border-sm"></span>
      </div>

      <h4 class="d-flex align-items justify-content-center gap-1">
        Inventory Optimizer
      </h4>

      <ng-container *ngIf="!isWarning; else warning">
        <!-- Sale case: Sale Price (read-only + trend + discount tag) + Your Price (editable) -->
        <ng-container *ngIf="repricingItem?.isSaleActive">
          <!-- Sale Price — read-only, derived from Your Price -->
          <div style="margin-bottom: 1rem">
            <span class="label-xs" [ngClass]="{
              'label-trend-inc': getEffectiveTrend() > 0,
              'label-trend-dec': getEffectiveTrend() < 0,
              'gray': getEffectiveTrend() === 0
            }">Sale Price</span>
            <div class="price-row">
              <span class="price-large" [ngClass]="{
                'price-new-inc': getEffectiveTrend() > 0,
                'price-new-dec': getEffectiveTrend() < 0,
                'gray': getEffectiveTrend() === 0
              }">{{ getDisplayCurrency(getDerivedSalePrice()) }}</span>
              <app-discount-tag
                [originalText]="'Your Price'"
                [originalPrice]="customPrice"
                [salePrice]="getDerivedSalePrice()"
                [hardSavePercent]="repricingItem?.saleSavePercent"
                [displayCurrency]="displayCurrency"
              ></app-discount-tag>
            </div>
          </div>

          <!-- Your Price — editable via price-editor, no discount tag -->
          <div style="margin-bottom: 1rem">
            <app-repricing-price-editor
              [displayCurrency]="displayCurrency"
              labelClass="label-xs gray"
              priceClass="price-xl"
              [isReadOnly]="isSyncDisabled"
              [customPrice]="customPrice"
              [targetPrice]="repricingItem?.newInboundPrice"
              [trendPercentage]="repricingItem?.trendPercentage"
              [effectiveTrend]="getEffectiveTrend()"
              [validationError]="validationError"
              [isEditing]="isEditing"
              [isMissingPrice]="repricingItem?.state === ItemPriceState.MISSING_PRICE"
              (customPriceChange)="customPrice = $event"
              (resetPrice)="resetCustomPrice()"
              (editToggled)="openEdit()"
              (priceCommitted)="commitEdit()">
            </app-repricing-price-editor>
          </div>
        </ng-container>

        <!-- No-sale case: Calculated (shown if proposed exceeds range) + Your Price (editable) -->
        <ng-container *ngIf="!repricingItem?.isSaleActive">
          <div style="margin-bottom: 1rem" class="d-flex gap-3 align-items-center">
            <ng-container *ngIf="repricingItem?.newInboundPrice !== repricingItem?.calculatedInboundPrice">
              <div class="price-calculated">
                <span class="label-xs label-calculated gray">Calculated</span>
                <div class="price-row">
                  <span class="price-strikethrough price-large">{{ getDisplayCurrency(repricingItem?.calculatedInboundPrice) }}</span>
                </div>
              </div>
              <div class="arrow-changes">&rarr;</div>
            </ng-container>

            <!-- Your Price — editable -->
            <app-repricing-price-editor
              [displayCurrency]="displayCurrency"
              labelClass="label-xs gray"
              priceClass="price-xl"
              [isReadOnly]="isSyncDisabled"
              [isExceedRange]="isExceedRange"
              [customPrice]="customPrice"
              [targetPrice]="repricingItem?.newInboundPrice"
              [trendPercentage]="repricingItem?.trendPercentage"
              [effectiveTrend]="getEffectiveTrend()"
              [validationError]="validationError"
              [isEditing]="isEditing"
              [isMissingPrice]="repricingItem?.state === ItemPriceState.MISSING_PRICE"
              (customPriceChange)="customPrice = $event"
              (resetPrice)="resetCustomPrice()"
              (editToggled)="openEdit()"
              (priceCommitted)="commitEdit()">
            </app-repricing-price-editor>
          </div>
        </ng-container>

        <!-- Details Grid -->
        <div class="details-grid">
          <div class="detail-card min-max-card">
            <div class="min-max-row">
              <div class="min-max-section">
                <app-repricing-price-editor
                  label="Min Price"
                  labelClass="label-xs gray"
                  [displayCurrency]="displayCurrency"
                  [showTrendBadge]="false"
                  [showSuggestedPrice]="true"
                  [nullPlaceholder]="'N/A'"
                  [isReadOnly]="isSyncDisabled"
                  [customPrice]="customMin"
                  [targetPrice]="repricingItem?.newMin"
                  [validationError]="minValidationError"
                  [isEditing]="isEditingMin"
                  (customPriceChange)="customMin = $event"
                  (resetPrice)="resetCustomMin()"
                  (editToggled)="openEditMin()"
                  (priceCommitted)="commitEditMin()">
                </app-repricing-price-editor>
              </div>
              <span class="min-max-separator">—</span>
              <div class="min-max-section">
                <app-repricing-price-editor
                  label="Max Price"
                  labelClass="label-xs gray"
                  [displayCurrency]="displayCurrency"
                  [showTrendBadge]="false"
                  [showSuggestedPrice]="true"
                  [nullPlaceholder]="'N/A'"
                  [isReadOnly]="isSyncDisabled"
                  [customPrice]="customMax"
                  [targetPrice]="repricingItem?.newMax"
                  [validationError]="maxValidationError"
                  [isEditing]="isEditingMax"
                  (customPriceChange)="customMax = $event"
                  (resetPrice)="resetCustomMax()"
                  (editToggled)="openEditMax()"
                  (priceCommitted)="commitEditMax()">
                </app-repricing-price-editor>
              </div>
            </div>
          </div>
        </div>
      </ng-container>

      <!-- Warning Alert -->
      <ng-template #warning>
        <div class="warning-box">
          <div class="warning-box__title">Cannot calculate pricing</div>
          <div class="warning-box__description">{{ warningMessage }}</div>
        </div>
      </ng-template>
    </div>
  </div>

  <div class="note">
    <ng-container *ngIf="isWarning">
      <strong>Note:</strong> Make sure you have forecasted for this item.
    </ng-container>

    <ng-container *ngIf="!isWarning">
      <strong>Notes:</strong>
      <ul class="notes-list">
        <li>
          <span class="text-danger">(*)</span> Once the
          <strong>New Price</strong> is out of range (Min Price - Max Price),
          the <strong>New Price</strong> will add/subtract
          <strong>{{ displayCurrency?.symbol }}1</strong> and update Min/Max
          Price correspondingly.
        </li>
        <li>
          Changes will <strong>take 15 minutes to reflect</strong> on Amazon
          Seller Central.
        </li>
      </ul>
    </ng-container>

    <div
      *ngIf="status === RecommendationStatus.DONE"
      class="alert alert-success mt-3 d-flex align-items-center"
    >
      <i class="fas fa-check-circle mr-2"></i>
      This recommendation has been successfully updated to Amazon Seller Central (ASC).
    </div>

    <div
      *ngIf="[RecommendationStatus.QUEUED, RecommendationStatus.IN_PROGRESS, RecommendationStatus.APPLIED].includes(status)"
      class="alert alert-info mt-3 d-flex align-items-center"
    >
      <i class="fas fa-info-circle mr-2"></i>
      {{ repricingItem?.isRevert ? 'This recommendation is being reverted and is waiting for reflection.' : 'This recommendation is being applied and is waiting for reflection.' }}
    </div>
  </div>

  <div class="actions">
    <button class="btn cancel" (click)="activeModal.dismiss('Cross click')">
      Cancel
    </button>
    <button
      *ngIf="!isSyncDisabled"
      type="button"
      class="btn save"
      [disabled]="isLoading || isWarning || isNoChanges || customPrice == null || (isEditing && validationError) || !!minMaxValidationError"
      [ngStyle]="{
        cursor: !isLoading ? 'pointer' : 'not-allowed'
      }"
      (click)="onSyncToAmazon($event)"
    >
      Sync to Amazon
    </button>
  </div>
</div>

